import React, { useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { styles, ScreenReaderText } from '@utils';
import breakpoints from '@rsc/ui-components/breakpoints';
import Button from '@rsc/ui-components/button/Button';
import { useLocalizedNavigation } from '@rsc/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';
import { useLocalizedYup } from '@rsc/ui-components/hooks';

const propTypes = {
  useQueryString: PropTypes.bool,
};

function NewsletterSignupFooter({ useQueryString }) {
  const { tt } = useSportdogInternationalContext({});
  const yup = useLocalizedYup();

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .emailLocalized()
          .requiredLocalized()
          .label(tt(s => s.layoutCommon.emailLabel)),
      }),
    [tt, yup]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const navigate = useLocalizedNavigation();
  // no location prop to use
  const currentLocation =
    typeof window !== 'undefined' && window.location.pathname;

  const onSubmit = (data, e) => {
    e.preventDefault();
    const url = useQueryString
      ? `/email-updates?email=${data.email}`
      : '/email-updates';
    navigate(url, {
      state: { ...data, prevPath: currentLocation },
    });
  };
  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="email">
            <ScreenReaderText>
              {tt(s => s.layoutCommon.emailAddressLabel)}
            </ScreenReaderText>
            <input
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('email')}
              placeholder="oz@sportdog.com"
            />
          </label>
          <Button type="submit">
            {tt(s => s.layoutCommon.emailAddressSubmitButton)}
          </Button>
        </div>

        <ErrorMessage errors={errors} name="email">
          {({ message }) => (
            <NewsletterErrorMessage>{message}</NewsletterErrorMessage>
          )}
        </ErrorMessage>
      </form>
    </Container>
  );
}

const Container = styled.div`
  @media (min-width: ${breakpoints.md}) {
    width: 65%;
  }

  @media (min-width: ${breakpoints.lg}) {
    width: 100%;
  }

  div {
    display: flex;
    align-items: center;
  }

  label {
    flex: 1;

    input {
      border: none;
      border-radius: 0.25rem;
      font-size: 0.875rem;
      padding: 0.35rem;
      width: 100%;
    }
  }

  button {
    margin-left: 0.5rem;
  }
`;

const NewsletterErrorMessage = styled.div`
  color: ${styles.colors.gray[400]};
  font-size: 1rem;
  padding-top: 0.5rem;
`;

NewsletterSignupFooter.propTypes = propTypes;
NewsletterSignupFooter.defaultProps = {
  useQueryString: false,
};

export default NewsletterSignupFooter;
